/**
 * Azamphone collection page: filters, sort and pagination without a page
 * reload (<az-collection>), the two-handle price slider (<az-price-range>)
 * and the "Show all" / search helpers of long filter lists.
 * Loaded by sections/az-main-collection.liquid.
 *
 * Every change requests the same URL a normal form submit would use, with
 * section_id added (Shopify Section Rendering API), and swaps the parts of
 * the section marked [data-swap]. The address bar is updated with
 * history.pushState, so filtered URLs stay shareable and Back/Forward work.
 * Without JavaScript, or when a request fails, the forms submit normally.
 *
 * Lifecycle: custom elements, so Theme Editor re-rendering is handled by
 * connected/disconnectedCallback; definitions are guarded.
 */
(() => {
  class AzCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      if (!this.sectionId || typeof window.fetch !== 'function' || typeof window.DOMParser !== 'function') return;
      this.requestId = 0;
      this.previewId = 0;

      this.handleChange = (event) => this.onChange(event);
      this.handleSubmit = (event) => this.onSubmit(event);
      this.handleClick = (event) => this.onClick(event);
      this.handleInput = (event) => this.onInput(event);
      this.handlePopstate = () => this.load(window.location.href, { push: false, scroll: false });

      this.addEventListener('change', this.handleChange);
      this.addEventListener('submit', this.handleSubmit);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('input', this.handleInput);
      window.addEventListener('popstate', this.handlePopstate);
    }

    disconnectedCallback() {
      if (!this.handleChange) return;
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('submit', this.handleSubmit);
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('input', this.handleInput);
      window.removeEventListener('popstate', this.handlePopstate);
      if (this.controller) this.controller.abort();
    }

    /* A changed filter control (checkbox, price input or slider, sort select). */
    onChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement) || target.matches('[data-filter-search]')) return;
      const form = target.form;
      if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;
      if (form.dataset.filtersForm === 'sheet' && target.name !== 'sort_by') {
        /* In the mobile sheet edits stay pending: only the result count updates. */
        this.preview(form);
        return;
      }
      this.load(this.urlFor(form), { push: true, scroll: false });
    }

    /* Apply / Show results / Sort buttons and Enter in a price field. */
    onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-filters-form], .az-sort__form')) return;
      event.preventDefault();
      const dialog = form.closest('dialog');
      this.load(this.urlFor(form), { push: true, scroll: false }).then(() => {
        if (dialog && dialog.open) dialog.close();
      });
    }

    /* "Show all" toggles and links that stay on this collection (active-filter
     * chips, Clear all, Reset, product type links, pagination). */
    onClick(event) {
      const toggle = event.target.closest('[data-filter-toggle]');
      if (toggle && this.contains(toggle)) {
        this.toggleList(toggle);
        return;
      }
      const link = event.target.closest('a[href]');
      if (!link || !this.contains(link) || event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target && link.target !== '_self') return;
      let url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (error) {
        return;
      }
      if (url.origin !== window.location.origin || url.pathname !== this.collectionPath()) return;
      event.preventDefault();
      const dialog = link.closest('dialog');
      const scroll = Boolean(link.closest('.az-pagination'));
      this.load(url.href, { push: true, scroll }).then(() => {
        if (dialog && dialog.open) dialog.close();
      });
    }

    /* Search box inside a long filter list: hides rows that do not match. */
    onInput(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('[data-filter-search]')) return;
      const list = this.listFor(input);
      if (!list) return;
      const query = input.value.trim().toLowerCase();
      list.classList.toggle('is-searching', query.length > 0);
      list.querySelectorAll('[data-filter-item]').forEach((item) => {
        const value = item.querySelector('.az-filters__value');
        const text = (value || item).textContent.toLowerCase();
        item.hidden = query.length > 0 && !text.includes(query);
      });
    }

    listFor(control) {
      const body = control.closest('.az-filters__body');
      return body ? body.querySelector('[data-filter-list]') : null;
    }

    toggleList(button) {
      const list = this.listFor(button);
      if (!list) return;
      const expanded = list.classList.toggle('is-expanded');
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? button.dataset.textLess : button.dataset.textMore;
    }

    collectionPath() {
      return new URL(this.dataset.url || window.location.pathname, window.location.origin).pathname;
    }

    /* The URL a plain submit of this form would request (empty values dropped). */
    urlFor(form) {
      const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
      const params = new URLSearchParams();
      new FormData(form).forEach((value, name) => {
        if (typeof value !== 'string' || value === '') return;
        if (name.startsWith('filter.') || name === 'sort_by') params.append(name, value);
      });
      url.search = params.toString();
      return url.href;
    }

    async fetchSection(url) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', this.sectionId);
      const response = await fetch(target.href, {
        signal: this.controller.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    }

    async load(url, { push, scroll }) {
      const id = ++this.requestId;
      this.setBusy(true);
      try {
        const doc = await this.fetchSection(url);
        const fresh = doc.querySelector('az-collection');
        if (!fresh) throw new Error('Section markup missing');
        this.swap(fresh);
        if (push && url !== window.location.href) {
          window.history.pushState({ azCollection: true }, '', url);
        }
        this.syncSort(url);
        if (scroll) this.scrollToResults();
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        window.location.assign(url);
      } finally {
        if (id === this.requestId) this.setBusy(false);
      }
    }

    /* Pending sheet edits: fetch the result count for the "Show N products" button. */
    preview(form) {
      const id = ++this.previewId;
      this.fetchSection(this.urlFor(form))
        .then((doc) => {
          if (id !== this.previewId) return;
          const source = doc.querySelector('[data-swap="sheet-count"]');
          const dialog = form.closest('dialog');
          const target = dialog ? dialog.querySelector('[data-swap="sheet-count"]') : null;
          if (source && target) target.textContent = source.textContent;
        })
        .catch(() => {});
    }

    /* Replace every [data-swap] region, keeping open/expanded groups and focus. */
    swap(fresh) {
      const open = new Map();
      this.querySelectorAll('details[data-filter-group]').forEach((group) => open.set(group.id, group.open));
      const expanded = new Set();
      this.querySelectorAll('[data-filter-list].is-expanded').forEach((list) => {
        const group = list.closest('details');
        if (group) expanded.add(group.id);
      });
      const active = document.activeElement;
      const activeId = active && this.contains(active) && active.id ? active.id : null;

      fresh.querySelectorAll('[data-swap]').forEach((source) => {
        const name = source.getAttribute('data-swap');
        const target = this.querySelector(`[data-swap="${name}"]`);
        if (target) target.innerHTML = source.innerHTML;
      });

      this.querySelectorAll('details[data-filter-group]').forEach((group) => {
        if (open.has(group.id)) group.open = open.get(group.id);
        if (expanded.has(group.id)) {
          const list = group.querySelector('[data-filter-list]');
          const button = group.querySelector('[data-filter-toggle]');
          if (list && button && !list.classList.contains('is-expanded')) this.toggleList(button);
        }
      });
      this.dataset.productsCount = fresh.dataset.productsCount || '';

      if (activeId) {
        const element = document.getElementById(activeId);
        if (element && typeof element.focus === 'function') element.focus({ preventScroll: true });
      }
    }

    /* Sort selects live outside the swapped regions: mirror the URL's sort. */
    syncSort(url) {
      const sort = new URL(url, window.location.origin).searchParams.get('sort_by') || this.dataset.defaultSort || '';
      if (!sort) return;
      this.querySelectorAll('select[name="sort_by"]').forEach((select) => {
        if (select.value !== sort) select.value = sort;
      });
    }

    scrollToResults() {
      const results = this.querySelector('[data-swap="results"]');
      if (!results) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const top = results.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
    }

    setBusy(busy) {
      this.classList.toggle('is-loading', busy);
      const results = this.querySelector('[data-swap="results"]');
      if (results) results.setAttribute('aria-busy', String(busy));
    }
  }

  /**
   * <az-price-range>: two overlapping range inputs (min / max) kept in sync
   * with the From / To number inputs, which are what the form submits.
   * Dragging a handle updates the inputs; typing moves the handles. The
   * handles never cross. Releasing a handle fires "change", which the
   * surrounding <az-collection> applies like any other filter change.
   */
  class AzPriceRange extends HTMLElement {
    connectedCallback() {
      this.rangeMin = this.querySelector('[data-price-range="min"]');
      this.rangeMax = this.querySelector('[data-price-range="max"]');
      this.inputMin = this.querySelector('[data-price-input="min"]');
      this.inputMax = this.querySelector('[data-price-input="max"]');
      this.fill = this.querySelector('[data-price-fill]');
      if (!this.rangeMin || !this.rangeMax || !this.inputMin || !this.inputMax) return;
      this.max = Number(this.rangeMin.max) || 0;

      this.handleRange = (event) => this.fromRange(event.target);
      this.handleInput = () => this.fromInputs();
      this.rangeMin.addEventListener('input', this.handleRange);
      this.rangeMax.addEventListener('input', this.handleRange);
      this.inputMin.addEventListener('input', this.handleInput);
      this.inputMax.addEventListener('input', this.handleInput);
      this.fromInputs();
    }

    disconnectedCallback() {
      if (!this.handleRange) return;
      this.rangeMin.removeEventListener('input', this.handleRange);
      this.rangeMax.removeEventListener('input', this.handleRange);
      this.inputMin.removeEventListener('input', this.handleInput);
      this.inputMax.removeEventListener('input', this.handleInput);
    }

    fromRange(range) {
      let min = Number(this.rangeMin.value);
      let max = Number(this.rangeMax.value);
      if (min > max) {
        if (range === this.rangeMin) {
          max = min;
          this.rangeMax.value = String(max);
        } else {
          min = max;
          this.rangeMin.value = String(min);
        }
      }
      /* An untouched edge submits nothing, so the URL stays clean. */
      this.inputMin.value = min > 0 ? String(min) : '';
      this.inputMax.value = max < this.max ? String(max) : '';
      this.paint(min, max);
    }

    fromInputs() {
      const min = this.clamp(this.inputMin.value, 0);
      const max = this.clamp(this.inputMax.value, this.max);
      const low = Math.min(min, max);
      const high = Math.max(min, max);
      this.rangeMin.value = String(low);
      this.rangeMax.value = String(high);
      this.paint(low, high);
    }

    clamp(value, fallback) {
      const number = parseFloat(value);
      if (!Number.isFinite(number)) return fallback;
      return Math.min(this.max, Math.max(0, number));
    }

    paint(min, max) {
      if (this.max <= 0) return;
      if (this.fill) {
        this.fill.style.left = `${(min / this.max) * 100}%`;
        this.fill.style.right = `${100 - (max / this.max) * 100}%`;
      }
      /* With both handles at the far end the min handle must stay on top. */
      this.rangeMin.classList.toggle('is-top', min > this.max * 0.95);
    }
  }

  if (!customElements.get('az-collection')) customElements.define('az-collection', AzCollection);
  if (!customElements.get('az-price-range')) customElements.define('az-price-range', AzPriceRange);
})();
